import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as mfaApi from 'src/api/mfa';
import { MfaChallengeData, MfaVerifyData, OrgMfaSettings } from 'src/api/mfa';
import { RequestOrganization, getRequestOrgScope } from 'src/api/utils';
import { UserStoreObject } from 'src/stores/UserStore';

const getOrgId = () => UserStoreObject.selectedOrg?.orgId;
const getOrgScope = () => UserStoreObject.selectedOrg?.orgId || UserStoreObject.selectedOrg?.orgNmIntrnl;

export const mfaStatusKey = (orgScope: number | string | undefined) => ['mfaStatus', orgScope] as const;
export const orgMfaSettingsKey = (orgId: number | undefined) => ['orgMfaSettings', orgId] as const;
export const mfaTrustedDevicesKey = (orgScope: number | string | undefined) => ['mfaTrustedDevices', orgScope] as const;

export const useMfaStatus = (enabled = true, organization?: RequestOrganization) => {
    const orgScope = getRequestOrgScope(organization);

    return useQuery({
        queryKey: organization ? [...mfaStatusKey(orgScope), UserStoreObject.usrNm] : mfaStatusKey(orgScope),
        queryFn: () => mfaApi.getMfaStatus(organization),
        enabled: enabled && Boolean(orgScope),
    });
};

export const useCreateMfaChallenge = (organization?: RequestOrganization) => {
    return useMutation({
        mutationFn: (data: MfaChallengeData) => mfaApi.createMfaChallenge(data, organization),
    });
};

export const useVerifyMfaChallenge = (organization?: RequestOrganization) => {
    const queryClient = useQueryClient();
    const orgScope = getRequestOrgScope(organization);

    return useMutation({
        mutationFn: (data: MfaVerifyData) => mfaApi.verifyMfaChallenge(data, organization),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mfaStatusKey(orgScope) });
        },
    });
};

export const useCreateTrustedDeviceSession = (organization?: RequestOrganization) => {
    const queryClient = useQueryClient();
    const orgScope = getRequestOrgScope(organization);

    return useMutation({
        mutationFn: () => mfaApi.createTrustedDeviceSession(organization),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mfaStatusKey(orgScope) });
        },
    });
};

export const useSetupTotp = (organization?: RequestOrganization) => {
    return useMutation({
        mutationFn: () => mfaApi.setupTotp(organization),
    });
};

export const useDeleteMfaMethod = () => {
    const queryClient = useQueryClient();
    const orgScope = getOrgScope();

    return useMutation({
        mutationFn: (methodId: number) => mfaApi.deleteMfaMethod(methodId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mfaStatusKey(orgScope) });
            queryClient.invalidateQueries({ queryKey: mfaTrustedDevicesKey(orgScope) });
        },
    });
};

export const useTrustedDevices = (enabled = true) => {
    const orgScope = getOrgScope();

    return useQuery({
        queryKey: mfaTrustedDevicesKey(orgScope),
        queryFn: mfaApi.getTrustedDevices,
        enabled: enabled && Boolean(orgScope),
    });
};

export const useDeleteTrustedDevice = () => {
    const queryClient = useQueryClient();
    const orgScope = getOrgScope();

    return useMutation({
        mutationFn: (trustedDeviceId: number) => mfaApi.deleteTrustedDevice(trustedDeviceId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mfaTrustedDevicesKey(orgScope) });
        },
    });
};

export const useSendEmailVerificationChallenge = () => {
    return useMutation({
        mutationFn: mfaApi.sendEmailVerificationChallenge,
    });
};

export const useVerifyEmail = () => {
    const queryClient = useQueryClient();
    const orgScope = getOrgScope();

    return useMutation({
        mutationFn: (data: { challengeId: number; code: string }) => mfaApi.verifyEmail(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mfaStatusKey(orgScope) });
            queryClient.invalidateQueries({ queryKey: mfaTrustedDevicesKey(orgScope) });
        },
    });
};

export const useUpdateEmailMfaEnabled = (organization?: RequestOrganization) => {
    const queryClient = useQueryClient();
    const orgScope = getRequestOrgScope(organization);

    return useMutation({
        mutationFn: (enabled: boolean) => mfaApi.updateEmailMfaEnabled(enabled, organization),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mfaStatusKey(orgScope) });
        },
    });
};

export const useOrgMfaSettings = () => {
    const orgId = getOrgId();

    return useQuery({
        queryKey: orgMfaSettingsKey(orgId),
        queryFn: mfaApi.getOrgMfaSettings,
    });
};

export const useUpdateOrgMfaSettings = () => {
    const queryClient = useQueryClient();
    const orgId = getOrgId();
    const orgScope = getOrgScope();

    return useMutation({
        mutationFn: (data: OrgMfaSettings) => mfaApi.updateOrgMfaSettings(data),
        onSuccess: (settings) => {
            queryClient.setQueryData(orgMfaSettingsKey(orgId), settings);
            queryClient.invalidateQueries({ queryKey: mfaStatusKey(orgScope) });
        },
    });
};
